/**
 * Utility for parsing URL parameters
 * Supports cover_ids and policy_ids for initializing comparison
 * Autoload is only enabled when autoload=1
 */

export interface UrlParams {
  coverIds: number[]
  policyIds: number[]
}

/**
 * Parse URL parameters (cover_ids and policy_ids)
 * Returns null unless autoload=1 is explicitly provided
 */
export function parseUrlParams(searchParams: URLSearchParams): UrlParams | null {
  const shouldAutoload = searchParams.get('autoload') === '1'
  if (!shouldAutoload) {
    return null
  }

  const coverIdsParam = searchParams.get('cover_ids')
  const policyIdsParam = searchParams.get('policy_ids')

  // If neither parameter is present, return null
  if (!coverIdsParam && !policyIdsParam) {
    return null
  }

  // Parse cover_ids (comma-separated)
  const coverIds = coverIdsParam
    ? coverIdsParam
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id) && id > 0)
    : []

  // Parse policy_ids (comma-separated)
  const policyIds = policyIdsParam
    ? policyIdsParam
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id) && id > 0)
    : []

  // At least cover_ids should be present
  if (coverIds.length === 0) {
    return null
  }

  return {
    coverIds,
    policyIds,
  }
}

/**
 * Check if autoload URL parameters are present
 */
export function hasUrlParams(searchParams: URLSearchParams): boolean {
  const shouldAutoload = searchParams.get('autoload') === '1'
  const hasComparisonParams = searchParams.has('cover_ids') || searchParams.has('policy_ids')
  return shouldAutoload && hasComparisonParams
}

/**
 * Clean URL parameters from URL after successful initialization
 */
export function cleanUrlParams(): void {
  const url = new URL(window.location.href)

  // Clear all query parameters once app initialization processing is complete
  const newUrl = url.pathname
  window.history.replaceState({}, '', newUrl)
}
